import React, { useState, useEffect } from 'react';
import { Search, MessageCircle, Stethoscope } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DoctorChatInterface from '../../components/Chat/DoctorChatInterface';
import { useSelector, useDispatch } from 'react-redux';
import { updatePatientChat, getPatientById } from '../../redux/patientSlice';
import { getSocket } from '../../socket';
import { formatDateChat } from '../../utils/dateHeader';
import CommonLoader from '../../components/CommonLoader';
import ErrorPage from '../../components/ErrorPage';
import { getFileURL } from '../../utils/fileURL';
import errorimg from '../../assets/img/colorkit (1).svg';

const DoctorChatPage = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userRole = localStorage.getItem('Role');
    const patientId = localStorage.getItem('ID');
    const { doctorChatList, selectedPatient, loading: patientLoading } = useSelector((state) => state.patient);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [socketState, setSocketState] = useState('connecting'); // 'connecting', 'connected', 'error'
    const [socketError, setSocketError] = useState(null);

    // Maximum length for search input
    const MAX_SEARCH_LENGTH = 50;

    // Fetch patient data if user is a patient
    useEffect(() => {
        if (userRole === 'patient' && patientId && !selectedPatient) {
            dispatch(getPatientById(patientId));
        }
    }, [dispatch, userRole, patientId, selectedPatient]);

    const formatTime = (date) => {
        if (!date) return "";

        // Convert to Date object if string
        const d = typeof date === "string" ? new Date(date) : date;

        // Invalid date guard
        if (isNaN(d.getTime())) return "";

        const now = new Date();
        const diff = now - d;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return t('chat.time.justNow');
        if (minutes < 60) return t('chat.time.minutesAgo', { minutes });
        if (hours < 24) return t('chat.time.hoursAgo', { hours });
        if (days < 7) return t('chat.time.daysAgo', { days });
        
        return d.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', day: 'numeric' });
    };

    useEffect(() => {
        // Check authentication first
        const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
        if (!isAuthenticated || !patientId) {
            setSocketState('error');
            setSocketError({
                message: t('doctorChatPage.pleaseLoginToAccessChat'),
                status: 401,
            });
            return;
        }

        // Get socket only if authenticated
        const chatSocket = getSocket();
        if (!chatSocket) {
            console.error("DoctorChatPage: Failed to get socket instance");
            setSocketState('error');
            setSocketError({
                message: t('doctorChatPage.unableToInitializeChat'),
                status: 401,
            });
            return;
        }

        console.log("DoctorChatPage: Socket instance obtained, connecting...");
        console.log("DoctorChatPage: Socket connected status:", chatSocket.connected);

        // Set connecting state
        setSocketState('connecting');
        setSocketError(null);

        // Handle socket connection
        const handleConnect = () => {
            console.log("DoctorChatPage: Socket connected successfully");
            setSocketState('connected');
        };

        // Handle socket connection errors
        const handleConnectError = (err) => {
            console.error("DoctorChatPage: Socket connection error:", err);
            setSocketState('error');
            setSocketError({
                message: err.message || t('doctorChatPage.failedToConnect'),
                status: err.status || 500,
            });
        };

        // Handle socket errors
        const handleError = (error) => {
            console.error("DoctorChatPage: Socket error:", error);
            setSocketState('error');
            setSocketError({
                message: error.message || t('doctorChatPage.chatServiceError'),
                status: error.status || 500,
            });
        };

        // Handle socket disconnection
        const handleDisconnect = (reason) => {
            console.log("DoctorChatPage: Socket disconnected:", reason);
            if (reason === 'io server disconnect' || reason === 'transport close' || reason === 'transport error') {
                setSocketState('error');
                setSocketError({
                    message: t('doctorChatPage.connectionLost'),
                    status: 500,
                });
            }
        };

        // Listen for initial chat list (sent automatically on connection)
        const handleChatListReady = (response) => {
            console.log("DoctorChatPage: chatListReady received:", response);
            if (response && response.success && response.data) {
                // Handle both array and object formats (backend may send object for single doctor)
                let doctorsArray = [];

                if (Array.isArray(response.data)) {
                    // If data is an array, process all doctors
                    doctorsArray = response.data;
                } else if (typeof response.data === 'object' && response.data.doctorId) {
                    // If data is an object (single doctor), convert to array
                    doctorsArray = [response.data];
                }

                if (doctorsArray.length > 0) {
                    console.log("DoctorChatPage: Processing", doctorsArray.length, "doctors");
                    // Update the chat list with all doctors
                    doctorsArray.forEach(doctor => {
                        // Map chatListReady structure to include unreadForPatient
                        const mappedDoctor = {
                            ...doctor,
                            unread: doctor.unread || 0,
                            unreadForPatient: doctor.unreadForPatient !== undefined ? doctor.unreadForPatient : doctor.unread || 0,
                            unreadForDoctor: doctor.unreadForDoctor || 0,
                        };
                        dispatch(updatePatientChat(mappedDoctor));
                    });
                } else {
                    console.log("DoctorChatPage: chatListReady received but no valid doctor data found");
                }
            } else {
                console.warn("DoctorChatPage: chatListReady received but response.success is false or data is missing:", response);
            }
        };

        // Listen for chat list updates (when new messages arrive or status changes)
        const handleChatListUpdate = (updatedChat) => {
            console.log("DoctorChatPage: chatListUpdate received:", updatedChat);
            dispatch(updatePatientChat(updatedChat));
        };

        // Register socket event listeners
        chatSocket.on('connect', handleConnect);
        chatSocket.on('connect_error', handleConnectError);
        chatSocket.on('error', handleError);
        chatSocket.on('disconnect', handleDisconnect);
        chatSocket.on('chatListReady', handleChatListReady);
        chatSocket.on('chatListUpdate', handleChatListUpdate);

        // Connect/reconnect socket - this will trigger automatic chatListReady event
        // If socket is already connected (e.g., from global connection in SupportSocketContext),
        // we need to reconnect so the server sends chatListReady again with the doctor list
        if (chatSocket.connected) {
            console.log("DoctorChatPage: Socket already connected, reconnecting to get chatListReady...");
            chatSocket.disconnect();
        }
        console.log("DoctorChatPage: Calling socket.connect()...");
        chatSocket.connect();

        return () => {
            if (chatSocket) {
                chatSocket.off('connect', handleConnect);
                chatSocket.off('connect_error', handleConnectError);
                chatSocket.off('error', handleError);
                chatSocket.off('disconnect', handleDisconnect);
                chatSocket.off('chatListReady', handleChatListReady);
                chatSocket.off('chatListUpdate', handleChatListUpdate);
            }
        }
    }, [patientId, dispatch]);

    const validateSearchInput = (value) => {
        // Remove any potentially dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check length
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            setSearchError(t('doctorChatPage.searchExceedsMaxLength', { maxLength: MAX_SEARCH_LENGTH }));
            return false;
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            setSearchError(t('doctorChatPage.searchOnlySpaces'));
            return false;
        }

        setSearchError('');
        return true;
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > 50 ? sanitized.substring(0, 50) : sanitized;

        // Always update searchQuery so the input stays in sync
        setSearchQuery(finalValue);

        // Validate input
        validateSearchInput(finalValue);
    };

    // Filter doctors based on search
    const doctors = Array.isArray(doctorChatList) ? doctorChatList : [];

    const filteredDoctors = doctors.filter(doctor =>
        doctor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doctor.doctorId?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleDoctorSelect = (doctor) => {
        setSelectedDoctor(doctor);
    };

    // Profile completion validation for patients
    if (userRole === 'patient' && selectedPatient && selectedPatient.profileStatus === 'incomplete' && !patientLoading) {
        return (
            <div className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center" data-testid="doctor-chat-page-profile-incomplete">
                <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                    <img
                        src={errorimg}
                        alt={t('patientappointment.profile_incomplete_alt', 'Profile Incomplete')}
                        className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain"
                        data-testid="doctor-chat-page-profile-incomplete-image"
                    />
                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4" data-testid="doctor-chat-page-profile-incomplete-title">
                        {t('patientappointment.unable_to_book', 'Unable to Access Chat')}
                    </h2>
                    <p className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4" data-testid="doctor-chat-page-profile-incomplete-message">
                        {t('patientappointment.profile_incomplete_message', 'Your profile is incomplete. Please complete your profile to access chat.')}
                    </p>
                    <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6 md:mb-8" data-testid="doctor-chat-page-update-profile-message">
                        {t('patientappointment.update_profile_message', 'Please update your profile information to continue.')}
                    </p>
                    <button
                        onClick={() => navigate('/patientProfile')}
                        className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 cursor-pointer bg-[#4ABDBD] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:bg-[#3aa8a8] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50"
                        data-testid="doctor-chat-page-go-to-profile-button"
                    >
                        {t('patientappointment.go_to_profile', 'Go to Profile')}
                    </button>
                </div>
            </div>
        );
    }

    // Show error page if socket connection failed
    if (socketState === 'error' && socketError) {
        return <ErrorPage error={socketError} pageName={t('chat.doctors', 'Chat Page')} />;
    }

    // Show loader while connecting or loading patient data
    if (socketState === 'connecting' || (userRole === 'patient' && patientLoading)) {
        return (
            <div className="h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg" data-testid="doctor-chat-page-loading">
                <CommonLoader />
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="h-full flex bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg w-full overflow-hidden"
            data-testid="doctor-chat-page-container"
        >
            {/* Left Sidebar - Doctor List */}
            <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="w-96 bg-white border-r border-gray-200 flex flex-col shadow-sm"
                data-testid="doctor-chat-sidebar"
            >
                {/* Header */}
                <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B]" data-testid="doctor-chat-sidebar-header">
                    <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2" data-testid="doctor-chat-sidebar-title">
                        <MessageCircle className="w-6 h-6" data-testid="doctor-chat-sidebar-icon" />
                        {t('chat.doctors')}
                    </h2>
                    {/* Search */}
                    <div className="relative" data-testid="doctor-chat-search-container">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 z-10" size={18} data-testid="doctor-chat-search-icon" />
                        <input
                            type="text"
                            placeholder={t('chat.searchDoctors')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={MAX_SEARCH_LENGTH}
                            className={`w-full pl-10 pr-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 bg-white/90 backdrop-blur-sm text-gray-800 placeholder-gray-500 ${searchError
                                ? 'border-red-300'
                                : 'border-white/30'
                                }`}
                            data-testid="doctor-chat-search-input"
                        />
                        {searchError && (
                            <p className="text-red-200 text-xs mt-1 absolute -bottom-5 left-0" data-testid="doctor-chat-search-error">{searchError}</p>
                        )}
                    </div>
                </div>

                {/* Doctor List */}
                <div className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50/50" data-testid="doctor-chat-list-container">
                    {filteredDoctors.length === 0 ? (
                        <div className="p-8 text-center text-gray-500" data-testid="doctor-chat-no-doctors">
                            <MessageCircle className="mx-auto mb-2 text-gray-400" size={48} data-testid="doctor-chat-no-doctors-icon" />
                            <p data-testid="doctor-chat-no-doctors-text">{t('chat.noDoctorsFound')}</p>
                        </div>
                    ) : (
                        <div className="p-3" data-testid="doctor-chat-list">
                            <AnimatePresence>
                                {filteredDoctors.map((doctor, index) => (
                                    <motion.button
                                        key={doctor.doctorId}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        onClick={() => doctor.chatEnabled === false ? null : handleDoctorSelect(doctor)}
                                        disabled={doctor.chatEnabled === false}
                                        title={doctor.chatEnabled === false ? "This doctor's plan doesn't include chat" : undefined}
                                        className={`w-full p-4 mb-2 rounded-xl transition-all text-left relative overflow-hidden ${doctor.chatEnabled === false ? 'opacity-60 cursor-not-allowed' : ''} ${selectedDoctor?.doctorId === doctor.doctorId
                                            ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white shadow-lg shadow-[#4ABDBD]/30'
                                            : 'bg-white hover:bg-gray-50 text-gray-800 shadow-sm hover:shadow-md'
                                            }`}
                                        data-testid={`doctor-chat-card-${doctor.doctorId}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="relative flex-shrink-0">
                                                {(doctor?.image || doctor?.avatar) ? (
                                                    <img
                                                        src={getFileURL(doctor?.image || doctor?.avatar)}
                                                        alt={doctor?.name}
                                                        className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-md"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            const fallback = e.target.parentElement.querySelector('.image-fallback');
                                                            if (fallback) fallback.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <div className={`image-fallback ${doctor?.image ? 'hidden' : 'flex'} w-12 h-12 rounded-full bg-gradient-to-r from-[#4ebfbf] to-[#3A9B9B] items-center justify-center border-2 border-white shadow-md`}>
                                                    <span className="text-white text-xl font-semibold">
                                                        {doctor?.name?.charAt(0)?.toUpperCase()}
                                                    </span>
                                                </div>

                                                {doctor?.isOnline && (
                                                    <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2 mb-1">
                                                    <div className={`font-semibold truncate ${selectedDoctor?.doctorId === doctor.doctorId
                                                        ? 'text-white'
                                                        : 'text-gray-800'
                                                        }`}>
                                                        {doctor?.name}
                                                    </div>
                                                    <div className={`text-xs ${selectedDoctor?.doctorId === doctor.doctorId
                                                        ? 'text-white/70'
                                                        : 'text-gray-400'}`}>
                                                        {doctor?.lastMessage?.createdAt ? formatDateChat(doctor?.lastMessage?.createdAt) : t('doctorChatPage.clickToStartChat')}
                                                        {/* {formatDateChat(doctor?.lastMessage?.createdAt)} */}
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between gap-2 mb-1">
                                                    <div className={`text-xs truncate ${doctor.chatEnabled === false
                                                        ? 'text-amber-600 italic'
                                                        : selectedDoctor?.doctorId === doctor.doctorId
                                                            ? 'text-white/90'
                                                            : 'text-gray-600'
                                                        }`}>
                                                        {doctor.chatEnabled === false
                                                            ? t('doctorChatPage.chatNotInPlan', "You can't chat with this doctor")
                                                            : doctor?.lastMessage?.message}
                                                    </div>
                                                    {(doctor?.unviewedCount > 0 || doctor?.unreadForPatient > 0 || doctor?.unread > 0) && (
                                                        <motion.span
                                                            initial={{ scale: 0 }}
                                                            animate={{ scale: 1 }}
                                                            className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-bold ${selectedDoctor?.doctorId === doctor.doctorId
                                                                ? 'bg-white/30 text-white'
                                                                : 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white'
                                                                }`}
                                                        >
                                                            {(() => {
                                                                const count = doctor?.unviewedCount || doctor?.unreadForPatient || doctor?.unread || 0;
                                                                return count > 99 ? '99+' : count;
                                                            })()}
                                                        </motion.span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.button>
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* Right Side - Chat Screen */}
            <motion.div
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="flex-1 flex flex-col bg-white"
                data-testid="doctor-chat-main-container"
            >
                {selectedDoctor ? (
                    <DoctorChatInterface socket={getSocket()} doctor={selectedDoctor} patientId={patientId} />
                ) : (
                    <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-white" data-testid="doctor-chat-empty-state">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="text-center"
                        >
                            <motion.div
                                animate={{ rotate: [0, 10, -10, 0] }}
                                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                            >
                                <MessageCircle className="mx-auto mb-4 text-gray-400" size={64} data-testid="doctor-chat-empty-icon" />
                            </motion.div>
                            <h3 className="text-xl font-semibold text-gray-700 mb-2" data-testid="doctor-chat-empty-title">{t('chat.selectDoctor')}</h3>
                            <p className="text-gray-500" data-testid="doctor-chat-empty-message">{t('chat.selectDoctorMessage')}</p>
                        </motion.div>
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
};

export default DoctorChatPage;
